import { useEffect, useRef, useState } from 'react'
import { reportHostState } from '../../../lib/problem-reporting/context'
import type { ProblemReportAdminRead } from '../../../api/generated/model/problemReportAdminRead'
import { Button } from '../../../components/ui/button'
import { useT } from '../../../i18n'
import { formatDateTime } from '../../../lib/datetime'
import { reportCopy, reportEventLabel, reportClosureLabel } from '../copy'
import PrivateReportImage from '../PrivateReportImage'
import { adminCopy } from './copy'
export default function ReportEvidence({
  report,
}: {
  report: ProblemReportAdminRead
}) {
  const t = useT(reportCopy),
    a = useT(adminCopy)
  const [copyResult, setCopyResult] = useState('')
  const lifetime = useRef<object | null>(null)
  useEffect(() => {
    const current = {}
    lifetime.current = current
    return () => { if (lifetime.current === current) lifetime.current = null }
  }, [report.id])
  return (
    <>
      <section
        aria-label={a.publicPreview}
        className="space-y-3 rounded-[var(--radius-control)] border border-[var(--color-border)] p-3"
      >
        <h4 className="font-medium">{a.publicPreview}</h4>
        <p className="text-xs text-[var(--color-text-muted)]">{a.publicHelp}</p>
        <p className="whitespace-pre-wrap break-words">{report.description}</p>
        <p>
          {t[report.kind]}
          {report.impact ? ` · ${t[report.impact]}` : ''}
        </p>
        <p>
          {a.route}: {report.route_name}
        </p>
        <p>
          {t.submitted}: {formatDateTime(report.created_at)}
          <br />
          {t.updated}: {formatDateTime(report.updated_at)}
        </p>
        {report.attachments.map((file) => (
          <PrivateReportImage
            key={file.id}
            id={report.id}
            fileId={file.id}
            audience="admin"
            mimeType={file.mime_type}
          />
        ))}
        {report.resolution_note && (
          <p className="whitespace-pre-wrap break-words">
            {report.resolution_note}
          </p>
        )}
        {report.fixed_release && (
          <p>
            {t.release}: {report.fixed_release}
          </p>
        )}
        {report.closure_reason && (
          <p>
            {reportClosureLabel(report.closure_reason, t)}
          </p>
        )}
        <h5 className="font-medium">{t.history}</h5>
        <ol className="space-y-3">
          {report.events
            .filter((event) => event.visibility === 'public')
            .map((event) => (
              <li
                key={event.id}
                className="border-l border-[var(--color-border)] pl-3"
              >
                <p>
                  {reportEventLabel(event, t, report.attachments.some(file => file.mime_type.startsWith('video/')))}
                </p>
                {event.public_note && (
                  <p className="whitespace-pre-wrap break-words">
                    {event.public_note}
                  </p>
                )}
                <time className="text-xs text-[var(--color-text-muted)]">
                  {formatDateTime(event.created_at)}
                </time>
              </li>
            ))}
        </ol>
      </section>
      <details className="space-y-3">
        <summary className="cursor-pointer font-medium">{a.technical}</summary>
        <dl className="space-y-2 break-words">
          <dt>{a.environment}</dt>
          <dd>{report.environment}</dd>
          <dt>{a.frontend}</dt>
          <dd>{report.frontend_release ?? '—'}</dd>
          <dt>{a.backend}</dt>
          <dd>{report.backend_release}</dd>
        </dl>
        <h5 className="font-medium">{a.requests}</h5>
        {(report.context.failed_requests ?? [])
          .slice(0, 5)
          .map((request, i) => (
            <div key={i} className="space-y-1 break-words">
              <p>
                {request.method} {request.path_template} ·{' '}
                {request.status ?? request.failure_kind}
              </p>
              {request.request_id ? (
                <>
                  <code className="block select-all break-all">
                    {request.request_id}
                  </code>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      const current = lifetime.current
                      const generation = reportHostState().generation
                      const active = () => current !== null && lifetime.current === current &&
                        reportHostState().generation === generation && reportHostState().phase === 'ready' &&
                        reportHostState().policy?.can_review
                      void Promise.resolve()
                        .then(() => {
                          if (active()) return navigator.clipboard.writeText(request.request_id!)
                        })
                        .then(
                          () => { if (active()) setCopyResult(a.copied) },
                          () => { if (active()) setCopyResult(a.copyFailed) },
                        )
                    }}
                  >
                    {a.copyId}
                  </Button>
                </>
              ) : (
                <p>{a.noRequestId}</p>
              )}
            </div>
          ))}
        {copyResult && <p role="status">{copyResult}</p>}
        <pre className="max-h-60 overflow-auto whitespace-pre-wrap break-all rounded-[var(--radius-control)] bg-[var(--color-bg-subtle)] p-3 text-xs">
          {JSON.stringify(
            {
              browser: report.context.browser,
              viewport: report.context.viewport,
              locale: report.context.locale,
              timezone: report.context.timezone,
              online: report.context.online,
              js_errors: report.context.js_errors?.slice(0, 5),
              actions: report.context.actions?.slice(0, 20),
            },
            null,
            2,
          )}
        </pre>
      </details>
      <details>
        <summary className="cursor-pointer font-medium">
          {a.internalHistory}
        </summary>
        <div className="mt-3 space-y-3 break-words">
          <p>
            {a.assignee}: {report.assignee_membership_id ?? '—'}
          </p>
          {report.release_evidence && (
            <p>
              {a.deployment}: {report.release_evidence}
            </p>
          )}
          {report.events.map((event) => (
            <div
              key={event.id}
              className="space-y-1 border-l border-[var(--color-border)] pl-3"
            >
              <p>
                {event.visibility === 'internal'
                  ? a.assigned
                  : reportEventLabel(event, t)}
              </p>
              {event.details.assignee_membership_id && (
                <p className="text-xs">
                  {a.assignee}: {event.details.assignee_membership_id}
                </p>
              )}
              {event.details.fixed_release && (
                <p>
                  {a.build}: {event.details.fixed_release}
                </p>
              )}
              {event.details.previous_fixed_release && (
                <p>
                  {a.previousBuild}: {event.details.previous_fixed_release}
                </p>
              )}
              {event.details.release_evidence && (
                <p>
                  {a.deployment}: {event.details.release_evidence}
                </p>
              )}
              {event.details.previous_release_evidence && (
                <p>
                  {a.previousDeployment}:{' '}
                  {event.details.previous_release_evidence}
                </p>
              )}
              <time className="text-xs">
                {formatDateTime(event.created_at)}
              </time>
            </div>
          ))}
        </div>
      </details>
    </>
  )
}
